import type { QuickbooksMapping } from '@contracts/client/QuickbooksMapping';
import { TransactionCurrency } from '@contracts/payment/Currency';
import InputAdornment from '@mui/material/InputAdornment';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Client } from '../domain/Client';
import { useUpdateQuickbooksMapping } from './hooks/useUpdateQuickbooksMapping';

interface Props {
  clientId: string;
  quickbooksMapping?: QuickbooksMapping;
}

const emptyMapping: QuickbooksMapping = {
  CAD: '',
  USD: '',
};

export const QuickbooksClientMapping = ({
  clientId,
  quickbooksMapping,
}: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [currentMapping, setCurrentMapping] = useState(
    quickbooksMapping ?? emptyMapping,
  );
  const { updateQuickbooksMapping } = useUpdateQuickbooksMapping({
    clientId,
    onSuccess: (client: Client) => {
      showToast({
        severity: ToastLevel.success,
        message: t('quickbooks.success'),
      });
      setCurrentMapping(client.quickbooksMapping!);
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('quickbooks.error'),
      });
    },
  });

  const submit = (currency: TransactionCurrency, quickbooksId: string) => {
    if (quickbooksId !== currentMapping[currency]) {
      updateQuickbooksMapping({ ...currentMapping, [currency]: quickbooksId });
    }
  };

  return (
    <Stack>
      <Typography component="h4" variant="body1" style={{ fontWeight: 500 }}>
        {t('quickbooks.title')}
      </Typography>

      <Typography>{t('quickbooks.subtitle')}</Typography>

      <Stack width="50%">
        <QuickbooksClientIdField
          currency={TransactionCurrency.CAD}
          quickbooksId={currentMapping.CAD}
          submit={submit}
        />
        <QuickbooksClientIdField
          currency={TransactionCurrency.USD}
          quickbooksId={currentMapping.USD}
          submit={submit}
        />
      </Stack>
    </Stack>
  );
};

interface QuickbooksClientIdFieldProps {
  currency: TransactionCurrency;
  quickbooksId: string;
  submit: (currency: TransactionCurrency, quickbooksId: string) => void;
}

const QuickbooksClientIdField = ({
  currency,
  quickbooksId,
  submit,
}: QuickbooksClientIdFieldProps) => {
  const [clientId, setClientId] = useState<string>(quickbooksId);
  const { t } = useTranslation();

  return (
    <TextInput
      style={{ marginTop: theme.spacing['4'] }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              {t(`quickbooks.${currency.toLowerCase()}`)}
            </InputAdornment>
          ),
        },
      }}
      label={t('quickbooks.clientId')}
      placeholder={t('quickbooks.placeholder')}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          submit(currency, clientId);
        }
      }}
      onChange={(event) => {
        setClientId(event.target.value);
      }}
      onBlur={() => {
        submit(currency, clientId);
      }}
      value={clientId}
    />
  );
};
